import { type ProfileSidebarAvatarV2_userProfile$key } from '#app/__generated__/relay/ProfileSidebarAvatarV2_userProfile.graphql.js';
import { Avatar } from '#app/components/Avatars/Avatar/Avatar.js';
import { getAvatarSize } from '#app/components/Avatars/Avatar/utils.js';
import { ShareProfileButton } from '#app/components/ShareProfileButton/ShareProfileButton.js';
import { WorkAvailability } from '#app/components/WorkAvailability/WorkAvailability.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useUserProfileAvailability } from '#app/hooks/useUserProfileAvailability.js';
import { formatName } from '#app/utilities/formatName.js';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { lazy, Suspense } from 'react';
import { css, styled } from 'styled-components';

const AvatarAndAvailabilityContainer = styled.div`
  display: grid;
  grid-auto-columns: 1fr;
  grid-auto-rows: 1fr;
  grid-template-columns: 1fr;
  grid-template-rows: 1fr 16px auto;
  gap: 0px 0px;
  width: 100%;
`;

const AvatarContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  grid-area: 1 / 1 / 3 / 2;
`;

const AvailabilityContainer = styled.div`
  grid-area: 2 / 1 / 4 / 2;
  z-index: 10;
  margin-left: auto;
  margin-right: auto;
`;

const StyledAvatar = styled(Avatar)`
  border: 1px solid ${({ theme }) => theme.colorsV2.gray30};
`;

const ShareButtonContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    align-items: flex-start;
    grid-area: 1 / 1 / 1 / 1;

    ${theme.mediaQueries.lg} {
      display: none;
    }
  `}
`;

type ProfileSidebarAvatarProps = {
  readonly avatarSize?: number;
  readonly disableEditing?: boolean;
  readonly hideMobileShareButton?: boolean;
  readonly openLinkInNewWindow?: boolean;
  readonly userProfileRef: ProfileSidebarAvatarV2_userProfile$key;
};

const LazyEditableAvatar = lazy(async () => {
  return {
    default: (await import('#app/components/EditableAvatar/EditableAvatar.js'))
      .EditableAvatar,
  };
});

const determineAvatarSize = (isMobile: boolean) => {
  const sizeValue = isMobile ? 'xxl' : 176;
  return getAvatarSize(sizeValue);
};

export const ProfileSidebarAvatarV2 = ({
  avatarSize,
  disableEditing = false,
  hideMobileShareButton,
  openLinkInNewWindow,
  userProfileRef,
}: ProfileSidebarAvatarProps) => {
  const data = useFragment<ProfileSidebarAvatarV2_userProfile$key>(
    graphql`
      fragment ProfileSidebarAvatarV2_userProfile on UserProfile {
        firstName
        lastName
        visitorCanEdit
        displayUsername
        hasCompletedIndyOnboarding
        avatarImage {
          id
          url
          uid
        }
        ...ShareProfileButton_userProfile
        ...useUserProfileAvailability_userProfile
        ...WorkAvailability_userProfile
      }
    `,
    userProfileRef,
  );
  const isMobile = useIsMobile();

  const { displayUsername, firstName, lastName, visitorCanEdit } = data;

  const fullName = formatName(
    {
      firstName,
      lastName,
    },
    'fullName',
  );

  const { showAvailability } = useUserProfileAvailability({
    userProfileRef: data,
  });

  const size =
    isMobile || !avatarSize ? determineAvatarSize(isMobile) : avatarSize;

  // we do this to stop the relay eslint rule from complaining about unused fields
  const avatarImage = data.avatarImage
    ? {
        id: data.avatarImage.id,
        uid: data.avatarImage.uid,
        url: data.avatarImage.url,
      }
    : null;

  const defaultAvatar = (
    <AvatarAndAvailabilityContainer>
      <AvatarContainer
        as="a"
        href={getRoute('/@username', { args: { username: displayUsername } })}
        {...(openLinkInNewWindow ? { target: '_blank' } : {})}
      >
        <StyledAvatar
          alt={`${fullName} profile image`}
          avatarImage={avatarImage}
          fallbackInitialsLength={2}
          fallbackInitialsTextStyle="secondLevelHeadline"
          fallbackName={fullName}
          loading="eager"
          size={size}
        />
      </AvatarContainer>
      {showAvailability ? (
        <AvailabilityContainer>
          <WorkAvailability userProfileRef={data} />
        </AvailabilityContainer>
      ) : null}
      {hideMobileShareButton ? null : (
        <ShareButtonContainer>
          <ShareProfileButton
            buttonShape="circular"
            size={48}
            userProfileRef={data}
            variant="V2Link"
          />
        </ShareButtonContainer>
      )}
    </AvatarAndAvailabilityContainer>
  );

  if (visitorCanEdit && !disableEditing) {
    return (
      <Suspense fallback={defaultAvatar}>
        <AvatarAndAvailabilityContainer>
          <AvatarContainer>
            <LazyEditableAvatar
              avatarImage={avatarImage}
              avatarSize={avatarSize}
              fallbackInitialsLength={2}
              fallbackInitialsTextStyle="secondLevelHeadline"
              fallbackName={fullName}
              shouldSaveProfileOnUpload
              showBorder
              variant="hover"
            />
          </AvatarContainer>
          {showAvailability ? (
            <AvailabilityContainer>
              <WorkAvailability userProfileRef={data} />
            </AvailabilityContainer>
          ) : null}
          {hideMobileShareButton ? null : (
            <ShareButtonContainer>
              <ShareProfileButton
                buttonShape="circular"
                size={48}
                userProfileRef={data}
                variant="V2Link"
              />
            </ShareButtonContainer>
          )}
        </AvatarAndAvailabilityContainer>
      </Suspense>
    );
  } else {
    return defaultAvatar;
  }
};
